import React, { useState, useEffect, useReducer } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { ICONS } from 'Components/utils/icon/icons';
import URI from 'urijs';
import { t } from 'Src/i18n';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { useAppContext } from 'Context/ReactAppContext';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { Hero } from 'Components/widgets/hero/Hero';
import { EditMyCriteriaPopin } from 'Components/widgets/edit-my-criteria/criteria/EditMyCriteriaPopin';
import { StepNav } from 'Components/frameworks/step-nav/StepNav';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { CriteriaWithPopOver } from 'Components/widgets/booking-breadcrumb/edit-criteria-header/CriteriaWithPopOver';
import { HeaderCriteriaDates } from 'Components/widgets/booking-breadcrumb/edit-criteria-header/HeaderCriteriaDates';
import { CriteriaDatesSelectorContent } from 'Components/widgets/booking-breadcrumb/edit-criteria-header/CriteriaDatesSelectorContent';
import { HeaderCriteriaAttendees } from 'Components/widgets/booking-breadcrumb/edit-criteria-header/HeaderCriteriaAttendees';
import { CriteriaAttendeesSelectorContent } from 'Components/widgets/booking-breadcrumb/edit-criteria-header/CriteriaAttendeesSelectorContent';
import { HeaderCriteriaTransport } from 'Components/widgets/booking-breadcrumb/edit-criteria-header/HeaderCriteriaTransport';
import { CriteriaTransportsSelectorContent } from 'Components/widgets/booking-breadcrumb/edit-criteria-header/CriteriaTransportsSelectorContent';
import {
  initialPopinsState,
  popinsActions,
  popinsReducer,
} from 'Components/widgets/booking-breadcrumb/headerPopins.reducer';
import { Toast } from 'Components/frameworks/toast/Toast';
import { COLORS } from 'Components/utils/color/colors';
import { getCriteriaFromProposal, searchNewProposalsNewCriteria } from './EditCriteria.utils';
import { TransportIncentive } from './TransportIncentive';
type Props = {
  activeStep: number;
  backgroundImageSrc: string;
  resort: Product;
  selectedProposal: Proposal;
  className?: string;
  items: Array<{
    iconName: IconName;
    id: string;
    label: string;
  }>;
  label?: string;
  lastActiveStep?: number;
  onClickStep: (step: number) => void;
  canEdit?: false;
  i18n: CMSContent;
  actionGA: string;
};
const EDIT_CRITERIA_TOAST_MS = 3000;
export const BookingBreadcrumb = ({
  resort,
  selectedProposal,
  activeStep,
  backgroundImageSrc,
  canEdit = true,
  className,
  items,
  label,
  lastActiveStep,
  i18n,
  onClickStep,
}: Props) => {
  const router = useRouter();
  const context = useAppContext();
  const [popinsState, dispatchPopin] = useReducer(popinsReducer, initialPopinsState);
  const dispatch = useDispatch();
  const { trackClickEvent } = useTrackEvent();
  const criteria = getCriteriaFromProposal({
    resort,
    proposal: selectedProposal,
    appContext: context,
  });
  const [newCriteria, setNewCriteria] = useState(criteria);
  const [shouldShowToastTimeOut, setShouldShowToastTimeOut] = useState(true);
  const isComingFromEditCriteria = typeof router.query.editCriteriaWarning !== 'undefined';
  useEffect(() => {
    if (isComingFromEditCriteria) {
      setShouldShowToastTimeOut(true);
      const toastTimer = setTimeout(() => {
        setShouldShowToastTimeOut(false);
        const newUri = new URI(router.asPath).removeQuery(['editCriteriaWarning']);
        router.replace(newUri.toString());
      }, EDIT_CRITERIA_TOAST_MS);
      return () => clearTimeout(toastTimer);
    }
  }, [router.query.editCriteriaWarning]);

  const cancelConfirmationPopin = () => {
    setNewCriteria(criteria);
    dispatchPopin({
      type: popinsActions.CRITERIA_CONFIRMATION_CLOSE,
    });
  };

  return (
    <div className="relative z-3 bg-black">
      <div className="absolute inset-0 flex items-center overflow-hidden opacity-50">
        <Hero className="flex-1" url={backgroundImageSrc} />
      </div>
      <SiteContainer>
        <div
          className={classnames(
            'relative z-3 flex h-130 flex-col justify-between py-10 text-white sm:flex-row md:h-80',
            className,
          )}
          data-testid="breadcrumb"
        >
          <StepNav
            activeStep={activeStep}
            ariaLabel={label}
            className="sm:self-end"
            items={items}
            lastActiveStep={lastActiveStep}
            onClick={onClickStep}
          />
          {canEdit && (
            <div className="order-first mb-10 flex justify-between gap-10 sm:order-last sm:mb-0 sm:py-5 md:gap-20">
              {/* DATES SELECTOR */}
              <CriteriaWithPopOver
                i18n={i18n}
                criteria={criteria}
                newCriteria={newCriteria}
                selectedProposal={selectedProposal}
                selectorComponent={HeaderCriteriaDates}
                popOverComponent={CriteriaDatesSelectorContent}
                setNewCriteria={setNewCriteria}
                dispatchPopin={dispatchPopin}
                onClickTracking={() =>
                  trackClickEvent(TRACK_EVENT_NAMES.CLICK_BREADCRUMB, {
                    detail_click: 'edit_date',
                  })
                }
                validateButtonCriteriaSelectorTracking={() =>
                  trackClickEvent(TRACK_EVENT_NAMES.CLICK_BREADCRUMB, {
                    detail_click: 'edit_date_validate',
                  })
                }
              />
              {/* ATTENDEES SELECTOR */}
              <CriteriaWithPopOver
                i18n={i18n}
                criteria={criteria}
                newCriteria={newCriteria}
                selectedProposal={selectedProposal}
                selectorComponent={HeaderCriteriaAttendees}
                popOverComponent={CriteriaAttendeesSelectorContent}
                setNewCriteria={setNewCriteria}
                dispatchPopin={dispatchPopin}
                onClickTracking={() =>
                  trackClickEvent(TRACK_EVENT_NAMES.CLICK_BREADCRUMB, {
                    detail_click: 'edit_participants',
                  })
                }
                validateButtonCriteriaSelectorTracking={() =>
                  trackClickEvent(TRACK_EVENT_NAMES.CLICK_BREADCRUMB, {
                    detail_click: 'edit_participants_validate',
                  })
                }
              />
              {/* TRANSPORTS SELECTOR */}
              <CriteriaWithPopOver
                i18n={i18n}
                criteria={criteria}
                newCriteria={newCriteria}
                selectedProposal={selectedProposal}
                selectorComponent={HeaderCriteriaTransport}
                popOverComponent={CriteriaTransportsSelectorContent}
                setNewCriteria={setNewCriteria}
                dispatchPopin={dispatchPopin}
                onClickTracking={() =>
                  trackClickEvent(TRACK_EVENT_NAMES.CLICK_BREADCRUMB, {
                    detail_click: 'edit_transport',
                  })
                }
                validateButtonCriteriaSelectorTracking={() =>
                  trackClickEvent(TRACK_EVENT_NAMES.CLICK_BREADCRUMB, {
                    detail_click: 'edit_transport_validate',
                  })
                }
                blockBeforeValidateComponent={TransportIncentive}
              />
            </div>
          )}
        </div>
        {/* MODALS */}
        {popinsState.criteriaConfirmation && (
          <EditMyCriteriaPopin
            mainIconName={ICONS.RECEIPT}
            onClickCancel={cancelConfirmationPopin}
            onClickConfirm={() => {
              const searchNewProposalArgs = {
                resort,
                criteria: newCriteria,
                router,
                context,
                proposalsLoaderLabel: t('booking.form.proposalsLoaderLabel'),
                selectedProposal,
                dispatch,
                dispatchPopin,
                setNewCriteria,
              };
              searchNewProposalsNewCriteria(searchNewProposalArgs);
              trackClickEvent(TRACK_EVENT_NAMES.CLICK_BREADCRUMB, {
                detail_click: 'edit_confirmation',
              });
            }}
            onCloseModal={cancelConfirmationPopin}
            confirmLabel={t('common.confirm')}
            cancelLabel={t('common.cancel')}
            titleLabel={t('booking.editCriteria.buttonLabel')}
            messageLabel={t('booking.editCriteria.alertMessage')}
            dataTestId="EditMyCriteriaPopin-criteriaConfirmation"
          />
        )}
        {popinsState.noProposalsFound && (
          <EditMyCriteriaPopin
            mainIconName={ICONS.WARNING}
            onClickConfirm={() => {
              setNewCriteria(criteria);
              dispatchPopin({
                type: popinsActions.CLOSE_ALL_POPINS,
              });
            }}
            onCloseModal={() =>
              dispatchPopin({
                type: popinsActions.NO_PROPOSAL_CLOSE,
              })
            }
            confirmLabel={t('booking.editCriteria.backCriteria')}
            titleLabel={t('booking.editCriteria.noAvailability')}
            messageLabel={t('booking.editCriteria.errorMessage')}
            dataTestId="EditMyCriteriaPopin-noProposalsFound"
            isErrored
          />
        )}
        {isComingFromEditCriteria && shouldShowToastTimeOut && (
          <Toast
            color={COLORS.GREEN}
            icon={ICONS.CHECK_OUTLINE}
            label={t('booking.editCriteria.modificationSuccessMessage')}
            onClose={() => {
              setShouldShowToastTimeOut(false);
            }}
            visible
          />
        )}
      </SiteContainer>
    </div>
  );
};
